import { useState, useRef } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import ImageSearch from './ImageSearch.jsx';
import EmbedVideo from './EmbedVideo.jsx';
import htmlSanitizer from '../../../lib/helpers/htmlSanitizer';
import { search } from '../mediaSearch';
import Tooltip from '../../Tooltip/Tooltip.jsx';
import { useNotification } from '../../Notification/useNotification';
import MissingSearchImage from '../../../../assets/images/missing-search-image.png';
import { uploadFile } from '../../AwsS3/ReactS3Client';
import LoadingComponent from '../../LoadingComponent/LoadingComponent.jsx';
import {
  MAX_ATTACHMENT_SIZE,
  SUCCESS_NO_CONTENT,
  SPECIAL_CHARACTER_REGEX,
  VALID_IMG_ATTACHMENTS,
} from '../data';
import { useGetAwsData } from '../hooks/useGetAwsData';

const providers = {
  images: ['pixabay', 'unsplash'],
  gifs: ['giphy'],
};

const allowedFileImageType = [
  'image/gif',
  'image/bmp',
  'image/jpeg',
  'image/png',
];

const RemoveMediaButton = ({ removeMedia, tooltipText }) => (
  <Li data-place="top" data-tip={tooltipText} onClick={removeMedia}>
    <Icon className="fa-regular fa-trash" />
  </Li>
);

const MediaUploader = ({ values, handleChange, removeMedia }) => {
  const initialSearchType = () => {
    if (!values.remoteMediaUrl) return 'images';

    return values.remoteMediaUrl.includes('.gif') ? 'gifs' : 'images';
  };

  const { t } = useTranslation('tileForm');
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(initialSearchType());
  const [contentToShow, setContentToShow] = useState('');
  const [results, setResults] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const imageInputRef = useRef(null);
  const awsImageData = useGetAwsData({ origin: 'image' });
  const { showError } = useNotification();

  const initImageUploader = () => {
    imageInputRef.current.click();
  };

  const executeSearch = async searchProviders => {
    const newResults = await search(searchText, searchProviders, 12);
    setResults(newResults);
    setContentToShow('search');
  };

  const searchOnInputEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch(providers[searchType]);
    }
  };

  const searchOnInputIconClick = () => {
    executeSearch(providers[searchType]);
  };

  const closeContent = () => {
    setSearchText('');
    setContentToShow('');
  };

  const changeSearchType = () => {
    const newProviderType = searchType === 'images' ? 'gifs' : 'images';

    if (searchText.length > 0) {
      executeSearch(providers[newProviderType]);
    }

    setSearchType(newProviderType);
  };

  const selectImage = ({ url, source, photographerUrl = '' }) => {
    handleChange({ media: { url, source, photographerUrl } });
  };

  const setEmbedVideo = ({ videoIframe }) => {
    handleChange({ media: { videoIframe } });
  };

  const handleAwsImageInput = e => {
    setIsUploading(true);

    if (!allowedFileImageType.includes(e.currentTarget.files[0].type)) {
      showError({ content: t('attachment.fileTypeNotAllowed') });
      setIsUploading(false);
      return;
    }

    if (e.currentTarget.files[0].size > MAX_ATTACHMENT_SIZE) {
      showError({ content: t('attachment.fileTooLarge') });
      setIsUploading(false);
      return;
    }

    e.preventDefault();
    const file = e.currentTarget.files[0];

    const fileName = file.name;
    const newFilename = fileName.replace(SPECIAL_CHARACTER_REGEX, '');

    uploadFile({
      ...awsImageData,
      file,
      fileName: newFilename,
      key: `${awsImageData.key}/${fileName}`,
    }).then(data => {
      if (data.status === SUCCESS_NO_CONTENT) {
        selectImage({
          url: data.location,
          source: 'image-upload',
        });
      } else {
        showError({ content: t('attachment.error') });
      }
      setIsUploading(false);
    });
  };

  return (
    <Wrapper>
      <MediaWrapper>
        {values.embedVideo ? (
          <EmbedVideoWrapper
            dangerouslySetInnerHTML={htmlSanitizer(values.embedVideo)}
          />
        ) : (
          <>
            {isUploading ? (
              <LoadingComponent marginBottom="150px" marginTop="100px" />
            ) : (
              <SelectedImage
                src={values.remoteMediaUrl || MissingSearchImage}
              />
            )}
            <ShadowOverlay />
          </>
        )}
        <ImageMenu>
          {!values.embedVideo && (
            <>
              <SearchWrapper>
                <MediaSearchInput
                  value={searchText}
                  onKeyPress={searchOnInputEnter}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder={t(`media.${searchType}SearchPlaceholder`)}
                />
                <Icon
                  className="fa-regular fa-magnifying-glass"
                  onClick={searchOnInputIconClick}
                />
              </SearchWrapper>
              <Li
                data-tip={t('media.gifTooltip')}
                data-place="top"
                onClick={changeSearchType}
              >
                <Icon className="fa-light fa-gif" />
              </Li>
              <Li
                data-tip={t('media.uploadTooltip')}
                data-place="top"
                onClick={initImageUploader}
              >
                <Icon className="fa-light fa-cloud-arrow-up" />
              </Li>
              <Li
                onClick={() => {
                  setContentToShow('embedVideo');
                  setSearchText('');
                }}
                data-tip={t('media.embedVideoTooltip')}
                data-place="top"
              >
                <Icon className="fa-regular fa-film" />
              </Li>
            </>
          )}
          {values.remoteMediaUrl && (
            <RemoveMediaButton
              removeMedia={removeMedia}
              tooltipText={
                values.embedVideo
                  ? t('media.removeVideo')
                  : t('media.removeImage')
              }
            />
          )}
        </ImageMenu>
        <UploadImageInput
          ref={imageInputRef}
          type="file"
          onChange={handleAwsImageInput}
          id="upload_image"
          name="upload_image"
          accept={VALID_IMG_ATTACHMENTS}
        />
        <Tooltip padding="4px 8px" />
      </MediaWrapper>
      {contentToShow === 'search' && (
        <ContentWrapper>
          <ImageSearch
            results={results}
            selectImage={selectImage}
            closeSearch={closeContent}
          />
        </ContentWrapper>
      )}
      {contentToShow === 'embedVideo' && (
        <ContentWrapper>
          <EmbedVideo
            setEmbedVideo={setEmbedVideo}
            closeContent={closeContent}
          />
        </ContentWrapper>
      )}
    </Wrapper>
  );
};

export default MediaUploader;

const Wrapper = styled.div`
  width: 100%;
`;

const MediaWrapper = styled.div`
  position: relative;
`;

const EmbedVideoWrapper = styled.div`
  width: 100%;
  height: 350px;
  background: ${themeGet('colors.black.100')};
  border-radius: 8px 8px 0px 0px;

  iframe {
    height: 100%;
    width: 100%;
    border-radius: 8px 8px 0px 0px;
  }
`;

const ContentWrapper = styled.div`
  padding: 20px 40px;
`;

const UploadImageInput = styled.input`
  display: none;
`;

const SelectedImage = styled.img`
  border-radius: 8px 8px 0px 0px;
  max-height: 500px;
  min-height: 100px;
  object-fit: cover;
  width: 100%;
`;

const ImageMenu = styled.ul`
  position: absolute;
  bottom: 10px;
  left: 10px;
  display: flex;
  align-items: center;
`;

const Li = styled.li`
  background: ${themeGet('colors.white')};
  height: 32px;
  border-radius: 8px;
  margin-right: 5px;
  min-width: 32px;
  padding: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SearchWrapper = styled(Li)`
  width: 200px;

  &:focus-within {
    outline: ${themeGet('colors.primary.100')} solid 1px;
  }
`;

const MediaSearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Montserrat';
`;

const Icon = styled.i`
  color: ${themeGet('colors.primary.100')};
`;

const ShadowOverlay = styled.div`
  display: block;
  width: 100%;
  height: 25%;
  position: absolute;
  bottom: 0;
  left: 0;

  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 1%,
    rgba(0, 0, 0, 0.25) 100%
  );
`;
